import { remove, concat } from 'ramda'
import { useState, useEffect } from 'react'
import { useDropzone } from 'react-dropzone'
import { useTranslation } from 'react-i18next'
import styled from 'styled-components'
import { Button } from '@/components/button'
import Icon from '@/components/icon'
import { fontWeights, colors } from '@/styles'
import { Box } from '@/components/structure'
import SecurePhotoUploadedIcon from '../chat-event/chat-icons/SecurePhotoUploaded.svg?react'

const SelectedImage = styled.img`
  height: 50%;
  width: 350px;
  max-height: 450px;
  max-width: 450px;
  margin: 0 auto;
  display: block;
  object-fit: contain;
`

const EmbedWrapper = styled.div`
  display: flex;
  justify-content: center;
  height: 50%;
  width: 100%;
`

const SelectEmbed = styled.object`
  height: 100%;
  width: 350px;
`

const NoPreview = styled.div`
  height: 50%;
  width: 350px;
`

const SelectedFile = ({ src, type, ...rest }) => {
  const isPDF = type === 'application/pdf'
  const hasPreview = type !== 'text/plain' && type !== 'application/msword' && type
  if (!hasPreview) return <NoPreview />
  return !isPDF ? (
    <SelectedImage src={src} {...rest} />
  ) : (
    <EmbedWrapper>
      <SelectEmbed data={`${src}#?toolbar=0&navpanes=0`} type={type} width="350px" {...rest} />
    </EmbedWrapper>
  )
}

const FilesWrapper = styled.div`
  display: grid;
  grid-gap: 15px;
  grid-template-columns: repeat(auto-fill, 150px);
  max-width: 810px;
  margin: 30px auto;
`

const FileWrapper = styled.div`
  height: 150px;
  width: 150px;
  outline: none;
  position: relative;
  :hover {
    img {
      transition: opacity 0.2s ease-in-out;
      opacity: 0.5;
    }
    svg {
      opacity: 1;
    }
  }
  ${({ active }) =>
    active &&
    `
    outline: 5px ${colors.secondary} solid;
  `}
`

const FileImage = styled.img`
  height: 100%;
  width: 100%;
  object-fit: cover;
`

const DocumentWrapper = styled.div`
  border: 1px solid ${colors.secondary};
  display: flex;
  justify-content: center;
  align-items: center;
  flex-direction: column;
  padding: 23px 20px;
  height: 100%;
  width: 100%;
  path,
  rect {
    stroke: ${colors.regular} !important;
  }
`

const InnerWrapper = styled.div`
  display: flex;
  justify-content: flex-end;
  flex-direction: column;
  align-items: center;
  height: 100%;
`

const DocumentText = styled.div`
  padding: 12px 0;
  font-weight: ${fontWeights.medium};
  cursor: default;
  font-size: 14px;
  color: ${colors.regular};
  text-align: center;
`

const FilePreview = ({ src, type, ...rest }) => {
  const { t } = useTranslation()
  const isPDF = type === 'application/pdf'
  const hasPreview = type !== 'text/plain' && type !== 'application/msword' && type && !isPDF
  const types = {
    'application/pdf': t('b.chat.view.send_files.types.pdf', 'PDF Document'),
    'application/msword': t('b.chat.view.send_files.types.word', 'Word Document'),
    'text/plain': t('b.chat.view.send_files.types.text', 'Text Document')
  }
  const docType = types[type] || types['text/plain']
  return hasPreview ? (
    <FileImage src={src} {...rest} />
  ) : (
    <DocumentWrapper>
      <InnerWrapper>
        <SecurePhotoUploadedIcon />
        <DocumentText>{docType}</DocumentText>
      </InnerWrapper>
    </DocumentWrapper>
  )
}

const RemoveIcon = styled(Icon)`
  position: absolute;
  top: 5px;
  right: 5px;
  z-index: 2;
  cursor: pointer;
  margin: 0;
  opacity: 0;
  transition: opacity 0.2s ease-out;
  path {
    fill: ${colors.regular};
  }
`

const AddBox = styled.div`
  border: 1px solid ${colors.secondary};
  display: flex;
  justify-content: center;
  align-items: center;
  height: 150px;
  width: 150px;
  transition: opacity 0.2s ease-in-out;
  cursor: pointer;
  :hover {
    opacity: 0.5;
  }
`

const ListView = ({ files, setFiles, sendAllFiles }) => {
  const { t } = useTranslation()
  const [selectedFile, setSelectedFile] = useState({})

  const handleAddFile = newFiles => setFiles(concat(files, newFiles))

  const { getInputProps, open } = useDropzone({
    accept: {
      'image/png': ['.png'],
      'image/jpg': ['.jpg', '.jpeg'],
      'image/jpeg': ['.jpg', '.jpeg'],
      'application/pdf': ['.pdf'],
      'application/msword': ['.doc'],
      'text/plain': ['.txt']
    },
    multiple: false,
    noKeyboard: true,
    onDrop: handleAddFile
  })

  useEffect(() => {
    if (!selectedFile.file) {
      setSelectedFile({ file: files[0], index: 0 })
    }
  }, [files, selectedFile.file])

  if (!selectedFile.file) return null
  const selectedFileUrl = URL.createObjectURL(selectedFile.file)
  const selectedFileType = selectedFile?.file?.type
  return (
    <>
      <SelectedFile src={selectedFileUrl} type={selectedFileType} />
      <FilesWrapper>
        {files.map((file, index) => {
          const type = file?.type
          const url = URL.createObjectURL(file)
          const deleteImage = e => {
            e.stopPropagation()
            const newFiles = remove(index, 1, files)
            setFiles(newFiles)

            if (index === selectedFile.index) {
              setSelectedFile({ file: newFiles[0], index: 0 })
            }
          }
          const onFileClick = () => {
            setSelectedFile({ file: files[index], index })
          }
          return (
            <FileWrapper
              key={`${file.name}-${index}`}
              active={index === selectedFile.index}
              onClick={onFileClick}
            >
              <RemoveIcon id="close" width={24} height={24} onClick={deleteImage} />
              <FilePreview src={url} type={type} />
            </FileWrapper>
          )
        })}
        {files.length < 10 && (
          <AddBox onClick={open}>
            <input {...getInputProps()} />
            <Icon width={50} height={50} id="plus" />
          </AddBox>
        )}
      </FilesWrapper>
      <Box top={24} justify="center">
        <Button
          onClick={() => {
            sendAllFiles()
            setFiles([])
          }}
          type="submit"
          theme="primary"
        >
          {t('b.chat.view.send_files.button', 'Send')}
        </Button>
      </Box>
    </>
  )
}

export default ListView
